import { createApp as createVueApp } from 'vue';
import { Store } from 'vuex';
import { initSentry, captureException } from '@core/plugins/sentry';
import { setSentryMetadata } from '@core/plugins/sentry/helper';

import i18n from '@monolith/legacy/services/i18n';
import { configureVue } from '@monolith/legacy/services/features';
import * as router from '@core/routes/main-router';
import { keepParameters } from '@core/utilities/parameters-persistance';
import Plugins from './plugins/plugins';
import GlobalComponents from './plugins/global-components-plugin';
import GlobalProperties from './plugins/global-properties-plugin';
import { createEventBus } from './plugins/global-event-bus-plugin';
import { isMetaTagMigrationEnabled, setInjectedContent } from '@modules/core/services/injected-content';
import App from './app.vue';
import { GlobalMetaTagName, StateMetaTagName } from '@modules/core/services/initial-state';
import { isEnabled } from '@monolith/legacy/services/features';
import { fetchAuthIdentity, fetchFeatureFlags } from '@modules/core/services/apiInitialState';
import { configureHttp } from './plugins/configure-http';
import http from '@monolith/legacy/services/api/http';

/** This function should be removed once we inject the user meta tag via API */
const isUserAuthenticated = (): boolean => {
  const userMeta = document.querySelector(`meta[name='state.user']`);
  return userMeta !== null && !!userMeta.getAttribute('content');
};

export const createApp = async (store: Store<unknown>) => {
  try {
    const { features, experiments } = await fetchFeatureFlags();

    if (features) {
      setInjectedContent(GlobalMetaTagName.Features, features);
    }
    if (experiments) {
      setInjectedContent(GlobalMetaTagName.Experiments, experiments);
    }

    // Fetch state from API for any migrated meta tags before mounting
    const needsAuthState =
      isMetaTagMigrationEnabled(StateMetaTagName.Brand) ||
      isMetaTagMigrationEnabled(StateMetaTagName.Retailer) ||
      isMetaTagMigrationEnabled(StateMetaTagName.Country) ||
      isMetaTagMigrationEnabled(StateMetaTagName.Currency) ||
      isMetaTagMigrationEnabled(StateMetaTagName.Stripe) ||
      isMetaTagMigrationEnabled(StateMetaTagName.User) ||
      isMetaTagMigrationEnabled(StateMetaTagName.ShowRequestedInformationForm) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.BrowserId);

    const needsPublicState =
      isMetaTagMigrationEnabled(StateMetaTagName.CurrenciesRates) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.ManagedLanguages) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.GlobalConfig) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.Categories) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.Tags) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.CDN) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.SentryDSN) ||
      isMetaTagMigrationEnabled(GlobalMetaTagName.Environment);

    const needsAnyState = needsAuthState || needsPublicState;

    // Check authentication only if authenticated state is needed
    // Public state (CurrenciesRates, ManagedLanguages) doesn't require authentication
    let isAuthenticated = false;

    if (needsAuthState) {
      const isAuthIdentityEnabled = isEnabled('FC-1431');
      isAuthenticated = isAuthIdentityEnabled ? (await fetchAuthIdentity()).isAuthenticated : isUserAuthenticated();
    }

    // Fetch state from API
    // Public APIs are always called, authenticated APIs only for authenticated users
    if (needsAnyState) {
      await store.dispatch('fetchStateFromApi', { isAuthenticated });
    }

    // Configure HTTP interceptors (CSRF, progress bar, 429 retry) after state
    // hydration so getGlobalConfig().aegis_enabled and getInjectedContent(CSRFToken)
    // resolve against populated cache.
    configureHttp(http(), store);

    router.initRouter();

    const app = createVueApp(App);

    configureVue(app);

    // Track UTM parameters from Ankorstore.com to my Ankorstore
    keepParameters();

    app.use(store);
    app.use(i18n);

    app.use(Plugins);
    app.use(GlobalProperties);
    app.use(GlobalComponents);

    initSentry(app, router.router);
    setSentryMetadata(store.state);
    app.use(createEventBus({ store }));

    if (router.router) {
      (router.router as any).app = app;
      app.use(router.router);
      await router.router.isReady();
    }

    if (!document.getElementById('teleport-wrapper')) {
      document.body.appendChild(document.createElement('div')).id = 'teleport-wrapper';
    }

    app.mount('#app');

    return app;
  } catch (e) {
    captureException(e);

    throw e;
  }
};
